import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react'
import Nango from '@nangohq/frontend'
import { marked } from 'marked'
import styles from './ChatBar.module.css'
import { useAuth } from '../auth/AuthContext.jsx'
import { useShowSqlSetting } from '../settings/ShowSqlContext.jsx'
import PlotlyChart from './PlotlyChart.jsx'

// ── Config ─────────────────────────────────────────────────────────────────────

const AI_URL = import.meta.env.VITE_AI_URL || 'http://localhost:8000'
const AI_WS_URL = AI_URL.replace(/^http/, 'ws')

const MANAGE_MODE_PAGES = new Set(['lake', 'connectors'])

// Human-readable labels for tool names shown during execution
const TOOL_LABELS = {
  run_sql_query:            'Querying data…',
  get_table_schema:         'Reading schema…',
  list_tables:              'Listing tables…',
  export_query_to_csv:      'Exporting to CSV…',
  inspect_uploaded_file:    'Inspecting file…',
  get_seed_upload_url:      'Preparing upload…',
  register_seed:            'Registering reference file…',
  list_seeds:               'Listing reference files…',
  delete_seed:              'Deleting reference file…',
  preview_seed:             'Previewing reference file…',
  add_dbt_source:           'Connecting data source…',
  get_dbt_sources:          'Reading sources…',
  get_dbt_manifest:         'Reading your data model…',
  list_dbt_models:          'Listing models…',
  get_dbt_model:            'Reading model…',
  create_dbt_model:         'Writing model…',
  update_dbt_model:         'Updating model…',
  delete_dbt_model:         'Deleting model…',
  document_prepared_columns: 'Documenting columns…',
  get_prepared_status:      'Checking model status…',
  get_analytics_status:     'Checking report status…',
  run_dbt_test:             'Testing data quality…',
  diff_draft:               'Diffing changes…',
  run_dbt_compile:          'Setting up your data pipeline…',
  run_dbt_seed:             'Loading into data lake…',
  get_compile_status:       'Waiting for job to complete…',
  promote_to_production:    'Promoting to production…',
  rollback_production:      'Rolling back…',
  configure_api_connection: 'Configuring connection…',
  verify_api_connection:    'Verifying authorization…',
  run_sync:                 'Starting data sync…',
  get_sync_status:          'Syncing data…',
  create_chart:             'Generating chart…',
  create_plotly_spec:       'Building chart…',
  get_kpi_definitions:      'Reading metric definitions…',
  get_dashboard_status:     'Checking dashboard health…',
  get_lake_refresh_error:   'Reading refresh error log…',
  define_metric:            'Adding metric…',
  list_metrics:             'Listing metrics…',
  delete_metric:            'Removing metric…',
}

// ── Utilities ──────────────────────────────────────────────────────────────────

function genId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2)
}

export function titleFromText(text) {
  const trimmed = text.trim()
  const cleaned = displayTitle(trimmed)
  if (cleaned !== trimmed) return cleaned
  const words = trimmed.split(/\s+/)
  return words.length <= 12 ? trimmed : words.slice(0, 12).join(' ') + '…'
}

// Cleans up a stored conversation title for display — handles both freshly
// generated titles and older titles that were truncated mid-bracket.
export function displayTitle(title) {
  const csvMatch = title.match(/^\[CSV upload: ([^—\]]+)/)
  if (csvMatch) return `Uploaded ${csvMatch[1].trim()}`
  const fileMatch = title.match(/^\[Attached file: ([^—\]]+)/)
  if (fileMatch) return `Uploaded ${fileMatch[1].trim()}`
  return title
}

function serverToLocalMsg(msg) {
  const sqlQueries = (msg.tool_calls ?? [])
    .filter(tc => tc.tool_name === 'run_sql_query' && tc.sql)
    .map(tc => tc.sql)
  return {
    id: msg.id,
    role: msg.role,
    ...(msg.role === 'user' ? { text: msg.content } : { answer: msg.content }),
    chartSpec: msg.chart_spec ?? null,
    sourceModels: msg.source_models ?? null,
    sqlQueries: sqlQueries.length ? sqlQueries : null,
    timestamp: msg.timestamp,
  }
}

export function relativeTime(iso) {
  const d = new Date(iso)
  const now = new Date()
  const days = Math.floor((now - d) / 86400000)
  if (days === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (days === 1) return 'Yesterday'
  if (days < 7) return d.toLocaleDateString([], { weekday: 'short' })
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export function lastPreview(messages) {
  if (!messages?.length) return ''
  const last = messages[messages.length - 1]
  let raw = last.role === 'user'
    ? last.text
    : (last.answer || '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\n/g, ' ')
  const csvMatch = raw.match(/^\[CSV upload: ([^—\]]+) — (\d+) rows[^\]]*\]/)
  if (csvMatch) raw = `Uploaded ${csvMatch[1].trim()} (${csvMatch[2]} rows)`
  const fileMatch = raw.match(/^\[Attached file: ([^—\]]+)/)
  if (fileMatch) raw = `Uploaded ${fileMatch[1].trim()}`
  return raw.length > 90 ? raw.slice(0, 90) + '…' : raw
}

export function groupByDate(convs) {
  const now = new Date()
  const out = {}
  for (const c of convs) {
    const days = Math.floor((now - new Date(c.createdAt)) / 86400000)
    const label = days === 0 ? 'Today' : days === 1 ? 'Yesterday' : days < 7 ? 'This Week' : 'Older'
    ;(out[label] ??= []).push(c)
  }
  return out
}

marked.use({ gfm: true, breaks: true })

// Markdown tables longer than this many body rows are collapsed behind a
// "Show all rows" toggle so they don't dominate the chat panel.
const TABLE_COLLAPSE_THRESHOLD = 8

function renderAnswer(text) {
  const html = marked.parse(text)
  const container = document.createElement('div')
  container.innerHTML = html
  container.querySelectorAll('table').forEach(table => {
    const rows = table.querySelectorAll('tbody tr')
    if (rows.length <= TABLE_COLLAPSE_THRESHOLD) return
    const scroll = document.createElement('div')
    scroll.className = `${styles.tableScroll} ${styles.tableCollapsed}`
    table.replaceWith(scroll)
    scroll.appendChild(table)
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.dataset.tableToggle = 'true'
    btn.className = styles.tableShowMoreBtn
    btn.textContent = `Show all ${rows.length} rows ▾`
    scroll.after(btn)
  })
  return container.innerHTML
}

// Event-delegated click handler for the table show-more/fewer toggle —
// flips the collapsed class on the table's scroll wrapper.
function handleAnswerClick(e) {
  const btn = e.target.closest('[data-table-toggle]')
  if (!btn) return
  const scroll = btn.previousElementSibling
  if (!scroll) return
  const collapsed = scroll.classList.toggle(styles.tableCollapsed)
  const rowCount = scroll.querySelectorAll('tbody tr').length
  btn.textContent = collapsed ? `Show all ${rowCount} rows ▾` : 'Show fewer rows ▴'
}

// ── Component ──────────────────────────────────────────────────────────────────

export const GROUP_ORDER = ['Today', 'Yesterday', 'This Week', 'Older']

const ACCEPTED_TYPES = '.csv,.tsv,.txt,.json,.md'

function parseAttachment(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => {
      const text = e.target.result
      const lines = text.split('\n').filter(Boolean)
      const preview = lines.slice(0, 5).join('\n')
      const truncated = lines.length > 5
      resolve({ name: file.name, size: file.size, text, preview, truncated, totalLines: lines.length })
    }
    reader.onerror = reject
    reader.readAsText(file)
  })
}

function attachmentToMessageText(attachment, userQuery) {
  const isTabular = /\.(csv|tsv)$/i.test(attachment.name)
  if (isTabular) {
    const truncNote = attachment.truncated ? ` — showing first ${attachment.text.split('\n').filter(Boolean).length} rows` : ''
    const header = `[CSV upload: ${attachment.name} — ${attachment.totalLines} rows${truncNote}]`
    const body   = `\`\`\`csv\n${attachment.text.slice(0, 12000)}\n\`\`\``
    const intent = userQuery || 'Analyze this data and answer any questions about it. At the end, offer to save it as permanent reference data if the user would like to use it in future sessions.'
    return `${header}\n\n${body}\n\n${intent}`
  }
  const header = `[Attached file: ${attachment.name} — ${attachment.totalLines} rows]\n\`\`\`\n${attachment.text.slice(0, 8000)}\n\`\`\``
  return userQuery ? `${header}\n\n${userQuery}` : header
}

// Extract display-friendly info from a user message that embedded file content.
// Returns { isFile, name, rows, query } or { isFile: false }.
function parseUserMessage(text) {
  const csv = text.match(/^\[CSV upload: (.+?) — (\d+) rows[^\]]*\]\n\n```csv\n[\s\S]*?```\n\n?([\s\S]*)$/)
  if (csv) return { isFile: true, name: csv[1], rows: parseInt(csv[2], 10), query: csv[3].trim() }
  const file = text.match(/^\[Attached file: (.+?) — (\d+) rows\]\n```\n[\s\S]*?```\n?\n?([\s\S]*)$/)
  if (file) return { isFile: true, name: file[1], rows: parseInt(file[2], 10), query: file[3].trim() }
  return { isFile: false }
}

const ChatBar = forwardRef(function ChatBar({
  userId,
  isOpen = false,
  onClose,
  suggestedPrompts = [],
  pageContext = '',
  pendingPrompt = '',
  onPendingPromptConsumed,
  onDataChanged,
  embedded = false,
  panelMode = false,
  onConversationsChange,
  onActiveConvChange,
  onChartGenerated,
  onTeamSharedChange,
}, ref) {
  const { getIdToken, apiFetch, effectiveTenantId } = useAuth()
  const { showSqlEnabled } = useShowSqlSetting()
  const isManageMode = MANAGE_MODE_PAGES.has(pageContext)

  const [conversations, setConversations] = useState([])
  const [convsLoaded, setConvsLoaded] = useState(false)
  const [teamShared, setTeamShared] = useState([])
  const [msgsLoading, setMsgsLoading] = useState(false)
  const [awaitingResponse, setAwaitingResponse] = useState(false)  // polling for server-side completion
  const pollRef = useRef(null)
  const [activeId, setActiveId] = useState(null)
  const [view, setView] = useState('list')       // 'list' | 'chat'
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [showSql, setShowSql] = useState({})
  const [expandedChart, setExpandedChart] = useState(null)
  const [attachment, setAttachment] = useState(null)  // parsed file object

  // Streaming state — cleared on each new message
  const [streamingText, setStreamingText] = useState('')
  const [streamingChart, setStreamingChart] = useState(null)
  const [, setActiveToolName] = useState(null)
  const [toolSteps, setToolSteps] = useState([])  // [{ name, label, done, error }]
  const [tokenWarning, setTokenWarning] = useState(null)

  const [expandedSteps, setExpandedSteps] = useState(new Set())
  const [pinnedMsgId, setPinnedMsgId] = useState(null)  // id of msg just pinned
  const [pendingNangoAuth, setPendingNangoAuth] = useState(null)  // { connection_id, provider, provider_display }

  const wsRef = useRef(null)
  const bottomRef = useRef(null)
  const inputRef = useRef(null)
  const fileRef = useRef(null)
  const creatingConvRef = useRef(false)

  const activeConv = conversations.find(c => c.id === activeId) ?? null

  useEffect(() => {
    if (panelMode) onConversationsChange?.(conversations)
  }, [conversations, panelMode, onConversationsChange])

  useEffect(() => {
    if (!expandedChart) return
    const onKey = (e) => { if (e.key === 'Escape') setExpandedChart(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [expandedChart])

  useEffect(() => {
    if (panelMode) onActiveConvChange?.(activeId)
  }, [activeId, panelMode, onActiveConvChange])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [activeConv?.messages?.length, loading, streamingText])

  useEffect(() => {
    if (isOpen && view === 'chat') {
      const t = setTimeout(() => inputRef.current?.focus(), 60)
      return () => clearTimeout(t)
    }
  }, [isOpen, view])

  useEffect(() => {
    if (isOpen && pendingPrompt) {
      setQuery(pendingPrompt)
      onPendingPromptConsumed?.()
      setView('chat')
      setTimeout(() => inputRef.current?.focus(), 60)
    }
  }, [isOpen, pendingPrompt, onPendingPromptConsumed])

  // Auto-resize textarea as content grows
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [query])

  // Load conversation list from server whenever the user or viewed tenant changes
  useEffect(() => {
    if (!userId) { setConvsLoaded(true); return }
    setConvsLoaded(false)
    const params = new URLSearchParams({ user_id: userId })
    if (effectiveTenantId) params.set('tenant_id', effectiveTenantId)
    fetch(`${AI_URL}/chats/?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.conversations ?? [])
        setConversations(list.map(c => ({
          id: c.id,
          title: c.title,
          createdAt: c.created_at,
          userId: c.user_id,
          shared: c.shared ?? false,
          messages: null,  // null = not yet loaded from server
        })))
      })
      .catch(() => {})
      .finally(() => setConvsLoaded(true))
  }, [userId, effectiveTenantId])

  // Load conversations shared across the tenant whenever the viewed tenant changes
  useEffect(() => {
    if (!effectiveTenantId) { setTeamShared([]); return }
    const params = new URLSearchParams({ tenant_id: effectiveTenantId })
    fetch(`${AI_URL}/chats/team?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.conversations ?? [])
        setTeamShared(list.map(c => ({
          id: c.id,
          title: c.title,
          createdAt: c.created_at,
          userId: c.user_id,
          shared: c.shared ?? false,
          messages: null,
        })))
      })
      .catch(() => {})
  }, [effectiveTenantId])

  useEffect(() => {
    if (panelMode) onTeamSharedChange?.(teamShared)
  }, [teamShared, panelMode, onTeamSharedChange])

  // Close WebSocket on unmount
  useEffect(() => {
    return () => { wsRef.current?.close() }
  }, [])

  // ── Navigation ─────────────────────────────────────────────────────────────

  const openNewChat = () => {
    setActiveId(null)
    setShowSql({})
    setQuery('')
    setStreamingText('')
    setStreamingChart(null)
    setActiveToolName(null)
    setTokenWarning(null)
    setAttachment(null)
    setView('chat')
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const parsed = await parseAttachment(file)
      setAttachment(parsed)
    } catch {
      // ignore unreadable files
    }
    e.target.value = ''
  }

  const clearAttachment = () => setAttachment(null)

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    setAwaitingResponse(false)
  }, [])

  // Start polling for a server-completed response (user left before it finished)
  const startPolling = useCallback((convId) => {
    stopPolling()
    setAwaitingResponse(true)
    pollRef.current = setInterval(async () => {
      try {
        const data = await fetch(`${AI_URL}/chats/${convId}/messages`).then(r => r.json())
        const msgs = (Array.isArray(data) ? data : (data.messages ?? [])).map(serverToLocalMsg)
        const lastMsg = msgs[msgs.length - 1]
        if (lastMsg?.role === 'assistant') {
          setConversations(prev => prev.map(c => c.id === convId ? { ...c, messages: msgs } : c))
          stopPolling()
        }
      } catch { /* ignore transient errors */ }
    }, 5000)
  }, [stopPolling])

  // Clean up poll on unmount
  useEffect(() => () => stopPolling(), [stopPolling])

  const openConv = useCallback(async (id, meta) => {
    stopPolling()
    setActiveId(id)
    setShowSql({})
    setQuery('')
    setStreamingText('')
    setStreamingChart(null)
    setActiveToolName(null)
    setToolSteps([])
    setTokenWarning(null)
    setView('chat')

    // A shared conversation opened from another user's list isn't in local
    // state yet — merge it in (lazily loading messages below) before continuing.
    let conv = conversations.find(c => c.id === id)
    if (!conv && meta) {
      conv = { ...meta, messages: meta.messages ?? null }
      setConversations(prev => [...prev, conv])
    }

    // Lazy-load messages from server the first time a conversation is opened
    if (conv && conv.messages === null) {
      setMsgsLoading(true)
      try {
        const data = await fetch(`${AI_URL}/chats/${id}/messages`).then(r => r.json())
        const msgs = (Array.isArray(data) ? data : (data.messages ?? [])).map(serverToLocalMsg)
        setConversations(prev => prev.map(c => c.id === id ? { ...c, messages: msgs } : c))
        // If the last message is a user turn, the AI may still be working server-side
        const lastMsg = msgs[msgs.length - 1]
        if (lastMsg?.role === 'user') startPolling(id)
      } catch {
        setConversations(prev => prev.map(c => c.id === id ? { ...c, messages: [] } : c))
      } finally {
        setMsgsLoading(false)
      }
    } else if (conv?.messages) {
      const lastMsg = conv.messages[conv.messages.length - 1]
      if (lastMsg?.role === 'user') startPolling(id)
    }
  }, [conversations, stopPolling, startPolling])

  useImperativeHandle(ref, () => ({
    openConversation: (id) => openConv(id),
    openSharedConversation: (conv) => openConv(conv.id, conv),
    startNewConversation: () => openNewChat(),
    setInput: (text) => {
      setView('chat')
      setQuery(text)
      setTimeout(() => inputRef.current?.focus(), 60)
    },
  }), [openConv, openNewChat])

  const goToList = () => setView('list')

  const deleteConv = useCallback(async (id, e) => {
    e.stopPropagation()
    setConversations(prev => prev.filter(c => c.id !== id))
    if (activeId === id) setView('list')
    try { await fetch(`${AI_URL}/chats/${id}`, { method: 'DELETE' }) } catch { /* local state already updated optimistically */ }
  }, [activeId])

  const toggleShare = useCallback(async (id) => {
    const conv = conversations.find(c => c.id === id)
    const shared = !conv?.shared
    setConversations(prev => prev.map(c => c.id === id ? { ...c, shared } : c))
    try {
      await fetch(`${AI_URL}/chats/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shared }),
      })
    } catch {
      // local state already updated optimistically
    }
  }, [conversations])

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async (text) => {
    const rawQ = (text ?? query).trim()
    const q = attachment ? attachmentToMessageText(attachment, rawQ) : rawQ
    if (!q || loading || creatingConvRef.current) return
    stopPolling()
    setQuery('')
    setAttachment(null)
    setStreamingText('')
    setStreamingChart(null)
    setActiveToolName(null)
    setToolSteps([])
    setTokenWarning(null)
    setPendingNangoAuth(null)

    // convId is the server chat ID — used for both local state key and WS URL
    let convId = activeId  // non-null when resuming an existing conversation

    if (!convId && !creatingConvRef.current) {
      creatingConvRef.current = true
      try {
        const resp = await fetch(`${AI_URL}/chats/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId || 'anon', tenant_id: effectiveTenantId, title: titleFromText(q) }),
        })
        if (!resp.ok) throw new Error('Failed to create conversation')
        const data = await resp.json()
        convId = data.id
        setActiveId(convId)
        setConversations(prev => [{
          id: convId,
          title: titleFromText(q),
          createdAt: data.created_at ?? new Date().toISOString(),
          userId,
          shared: false,
          messages: [],
        }, ...prev])
      } catch {
        setLoading(false)
        return
      } finally {
        creatingConvRef.current = false
      }
    }

    // Add user message to local state
    const userMsg = {
      id: genId(),
      role: 'user',
      text: q,
      timestamp: new Date().toISOString(),
    }
    setConversations(prev =>
      prev.map(c => c.id === convId ? { ...c, messages: [...(c.messages ?? []), userMsg] } : c)
    )
    setLoading(true)

    // Open WebSocket — attach Cognito ID token for server-side auth.
    // ID tokens include custom:tenant_id; access tokens do not.
    // In dev bypass mode getIdToken() returns null; the server accepts
    // tokenless connections when DEV_AUTH_BYPASS=true.
    const token = await getIdToken()
    const wsUrl = token
      ? `${AI_WS_URL}/chats/${convId}/stream?token=${encodeURIComponent(token)}`
      : `${AI_WS_URL}/chats/${convId}/stream`
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    let fullText = ''
    let currentChart = null
    let steps = []  // local closure mirror of toolSteps
    let sqlCalls = []  // SQL text for every run_sql_query call this turn, in order
    let hadToolResult = false  // inject \n\n between text segments separated by tool calls

    ws.onopen = () => {
      ws.send(JSON.stringify({
        message: q,
        user_id: userId || 'anon',
        tenant_id: effectiveTenantId || userId || 'anon',
        page_context: pageContext,
      }))
    }

    ws.onmessage = ({ data }) => {
      let event
      try { event = JSON.parse(data) } catch { return }

      switch (event.type) {
        case 'text_delta':
          if (hadToolResult && fullText.length > 0) {
            fullText += '\n\n'
            hadToolResult = false
          }
          fullText += event.text
          setStreamingText(fullText)
          break
        case 'tool_start':
        case 'tool_running': {
          const toolName = event.tool_name
          setActiveToolName(toolName)
          // Add step only if there isn't already a running entry for this tool (dedup polls)
          const alreadyRunning = steps.some(s => s.name === toolName && !s.done && !s.error)
          if (!alreadyRunning) {
            steps = [...steps, { name: toolName, label: TOOL_LABELS[toolName] ?? `${toolName}…`, done: false, error: false }]
            setToolSteps(steps)
          }
          break
        }
        case 'tool_result':
        case 'tool_error': {
          setActiveToolName(null)
          hadToolResult = true
          const isError = event.type === 'tool_error'
          // Mark the last running entry for this tool as done or error
          const idx = [...steps].reverse().findIndex(s => s.name === event.tool_name && !s.done && !s.error)
          if (idx >= 0) {
            const realIdx = steps.length - 1 - idx
            steps = steps.map((s, i) => i === realIdx ? { ...s, done: !isError, error: isError } : s)
            setToolSteps(steps)
          }
          if (event.tool_name === 'run_sql_query' && !isError && event.sql) {
            sqlCalls = [...sqlCalls, event.sql]
          }
          if (event.tool_name === 'configure_api_connection' && event.result?.status === 'pending_auth') {
            setPendingNangoAuth({
              connection_id:    event.result.connection_id,
              provider:         event.result.provider,
              provider_display: event.result.provider_display || event.result.provider,
            })
          }
          if (event.tool_name === 'register_seed' && !isError) {
            onDataChanged?.()
          }
          break
        }
        case 'chart':
          currentChart = {
            spec: event.spec,
            sourceModels: event.source_models ?? null,
            recipe: event.recipe ?? null,
            sql: event.source_sql ?? null,
          }
          setStreamingChart(event.spec)
          if (panelMode) onChartGenerated?.({ spec: event.spec })
          break
        case 'warning':
          setTokenWarning(event.text)
          break
        case 'error':
          // Server hit the token limit — the error text is the response
          fullText = event.text
          ws.close()
          break
        case 'done':
          ws.close()
          break
      }
    }

    ws.onclose = () => {
      wsRef.current = null
      setLoading(false)
      setActiveToolName(null)
      const captured = fullText
      const capturedChart = currentChart
      const capturedSql = sqlCalls
      // Mark any steps still running as done (WS closed cleanly)
      const capturedSteps = steps.map(s => s.done || s.error ? s : { ...s, done: true })
      setStreamingText('')
      setStreamingChart(null)
      setToolSteps([])

      if (captured || capturedChart || capturedSteps.length > 0) {
        const aiMsg = {
          id: genId(),
          role: 'assistant',
          answer: captured,
          chartSpec: capturedChart?.spec ?? capturedChart ?? null,
          sourceModels: capturedChart?.sourceModels ?? null,
          chartRecipe: capturedChart?.recipe ?? null,
          sql: capturedChart?.sql ?? null,
          sqlQueries: capturedSql.length > 0 ? capturedSql : undefined,
          toolSteps: capturedSteps.length > 0 ? capturedSteps : undefined,
          timestamp: new Date().toISOString(),
        }
        setConversations(prev =>
          prev.map(c => c.id === convId
            ? { ...c, messages: [...(c.messages ?? []), aiMsg] }
            : c
          )
        )
      }
    }

    ws.onerror = () => {
      setLoading(false)
      setActiveToolName(null)
      setStreamingText('')
      wsRef.current = null
    }
  }, [activeId, activeConv, query, loading, userId, effectiveTenantId, pageContext, setConversations, stopPolling])

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit() }
  }

  const pinMessage = useCallback(async (msg, userQuestion) => {
    const title = userQuestion
      ? (userQuestion.length > 72 ? userQuestion.slice(0, 72) + '…' : userQuestion)
      : (msg.answer || '').split('\n')[0].replace(/\*\*/g, '').slice(0, 72) || 'Chart'
    try {
      await apiFetch('/api/dashboard/pin', {
        method: 'POST',
        body: JSON.stringify({
          title,
          content: msg.answer || '',
          ...(msg.chartSpec ? { chart_spec: msg.chartSpec } : {}),
          ...(msg.sourceModels?.length ? { source_models: msg.sourceModels } : {}),
          ...(msg.chartRecipe ? { chart_recipe: msg.chartRecipe } : {}),
          ...(msg.sql ? { sql: msg.sql } : {}),
        }),
      })
      onDataChanged?.()
      setPinnedMsgId(msg.id)
      setTimeout(() => setPinnedMsgId(null), 2500)
    } catch {
      // best-effort — pin failed silently, no UI feedback needed
    }
  }, [apiFetch])

  const handleNangoAuth = useCallback(async () => {
    if (!pendingNangoAuth) return
    try {
      const sessionResp = await apiFetch('/api/connectors/nango-session', {
        method: 'POST',
        body: JSON.stringify({ provider: pendingNangoAuth.provider }),
      })
      if (!sessionResp.ok) throw new Error(`Session request failed: ${sessionResp.status}`)
      const { token } = await sessionResp.json()
      const nango = new Nango({ connectSessionToken: token })
      await nango.auth(pendingNangoAuth.provider, pendingNangoAuth.connection_id)
      const auth = pendingNangoAuth
      setPendingNangoAuth(null)
      handleSubmit(`I've completed the authorization for connection ${auth.connection_id}`)
    } catch (err) {
      console.error('Nango auth error:', err)
    }
  }, [pendingNangoAuth, handleSubmit, apiFetch])

  const groups = groupByDate(conversations)

  // ── Render ─────────────────────────────────────────────────────────────────

  const Shell = (embedded || panelMode) ? 'div' : 'aside'
  const shellClass = (embedded || panelMode)
    ? styles.panelEmbedded
    : `${styles.panel} ${isOpen ? styles.panelOpen : ''}`

  return (
    <Shell className={shellClass}>
      <div className={styles.inner}>

        {!panelMode && (
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              {view === 'chat' ? (
                <button className={styles.backBtn} onClick={goToList}>← Chats</button>
              ) : (
                <span className={styles.panelTitle}>
                  <span className={styles.titleIcon}>◈</span> Ask AI
                </span>
              )}
              {isManageMode && (
                <span className={styles.modeBadge}>Manage</span>
              )}
            </div>
            <div className={styles.headerRight}>
              {view === 'list' && (
                <button className={styles.newBtn} onClick={openNewChat} title="New conversation">
                  + New
                </button>
              )}
              {!embedded && <button className={styles.closeBtn} onClick={onClose} title="Close">✕</button>}
            </div>
          </div>
        )}

        {/* List view */}
        {!panelMode && view === 'list' && (
          <div className={styles.listScroll}>
            {!convsLoaded ? (
              <div className={styles.emptyList}>
                <div className={styles.emptyListIcon} style={{ opacity: 0.3 }}>◈</div>
                <p className={styles.emptyListText} style={{ opacity: 0.4 }}>Loading…</p>
              </div>
            ) : conversations.length === 0 ? (
              <div className={styles.emptyList}>
                <div className={styles.emptyListIcon}>◈</div>
                <p className={styles.emptyListText}>No conversations yet</p>
                <button className={styles.emptyNewBtn} onClick={openNewChat}>
                  Start your first chat →
                </button>
              </div>
            ) : (
              <>
                {GROUP_ORDER.filter(g => groups[g]).map(label => (
                  <div key={label} className={styles.group}>
                    <div className={styles.groupLabel}>{label}</div>
                    {groups[label].map(conv => (
                      <div
                        key={conv.id}
                        role="button"
                        tabIndex={0}
                        className={`${styles.convItem} ${conv.id === activeId ? styles.convActive : ''}`}
                        onClick={() => openConv(conv.id)}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && openConv(conv.id)}
                      >
                        <div className={styles.convRow}>
                          <span className={styles.convTitle}>{displayTitle(conv.title)}</span>
                          <span className={styles.convTime}>{relativeTime(conv.createdAt)}</span>
                        </div>
                        <div className={styles.convPreview}>{lastPreview(conv.messages)}</div>
                        {conv.shared && <span className={styles.sharedTag}>⤴ Shared</span>}
                        <button
                          className={styles.deleteBtn}
                          onClick={e => deleteConv(conv.id, e)}
                          title="Delete"
                        >×</button>
                      </div>
                    ))}
                  </div>
                ))}

                <div className={styles.group}>
                  <div className={styles.groupLabel}>Team Prompts</div>
                  {teamShared.length > 0 ? (
                    teamShared.map(conv => (
                      <button
                        key={conv.id}
                        className={styles.convItem}
                        onClick={() => openConv(conv.id, conv)}
                      >
                        <div className={styles.convRow}>
                          <span className={styles.convTitle}>{displayTitle(conv.title)}</span>
                          <span className={styles.teamBadge}>{conv.userId}</span>
                        </div>
                        <div className={styles.convPreview}>{lastPreview(conv.messages)}</div>
                      </button>
                    ))
                  ) : (
                    <div className={styles.teamHint}>
                      Conversations shared by your team will appear here.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Chat view */}
        {(panelMode || view === 'chat') && (
          <>
            {activeConv && (
              <div className={styles.chatTitleBar}>
                {!panelMode && <span className={styles.chatTitle}>{activeConv.title}</span>}
                <button
                  className={`${styles.shareBtn} ${activeConv.shared ? styles.shareBtnOn : ''}`}
                  onClick={() => toggleShare(activeConv.id)}
                  title={activeConv.shared ? 'Shared with team — click to unshare' : 'Share with team'}
                >
                  ⤴ {activeConv.shared ? 'Shared' : 'Share'}
                </button>
              </div>
            )}

            <div className={styles.thread}>
              {msgsLoading && (
                <div className={styles.emptyState}>
                  <div className={styles.thinking}><span /><span /><span /></div>
                </div>
              )}

              {!msgsLoading && !activeConv?.messages?.length && !loading && (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>◈</div>
                  {suggestedPrompts.length > 0 && (
                    <>
                      <div className={styles.emptyLabel}>
                        {isManageMode ? 'Configure your data lake…' : 'Try asking…'}
                      </div>
                      <div className={styles.chips}>
                        {suggestedPrompts.map((p, i) => (
                          <button key={i} className={styles.chip} onClick={() => handleSubmit(p)}>
                            {p}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeConv?.messages?.map((msg, i) => (
                <div key={msg.id} className={`${styles.msg} ${styles[msg.role]}`}>
                  {msg.role === 'user' ? (
                    <div className={styles.userBubble}>
                      {(() => {
                        const parsed = parseUserMessage(msg.text)
                        if (parsed.isFile) return (
                          <>
                            <div className={styles.fileUploadChip}>
                              <span className={styles.fileUploadIcon}>⊞</span>
                              <span className={styles.fileUploadName}>{parsed.name}</span>
                              <span className={styles.fileUploadMeta}>{parsed.rows.toLocaleString()} rows</span>
                            </div>
                            {parsed.query && <div className={styles.fileUploadQuery}>{parsed.query}</div>}
                          </>
                        )
                        return msg.text
                      })()}
                    </div>
                  ) : (
                    <div className={styles.assistantBubble}>
                      <div className={styles.aiLabel}>
                        <span className={styles.aiIcon}>◈</span> Structura AI
                        {isManageMode && <span className={styles.modeLabelInline}>Manage</span>}
                      </div>
                      {msg.toolSteps?.some(s => !s.error) && (() => {
                        const steps = msg.toolSteps.filter(s => !s.error)
                        const isOpen = expandedSteps.has(msg.id)
                        return (
                          <div className={styles.stepSummaryWrap}>
                            <button
                              className={styles.stepSummary}
                              onClick={() => setExpandedSteps(prev => {
                                const next = new Set(prev)
                                isOpen ? next.delete(msg.id) : next.add(msg.id)
                                return next
                              })}
                            >
                              ↳ {steps.length} step{steps.length !== 1 ? 's' : ''} {isOpen ? '▲' : '›'}
                            </button>
                            {isOpen && (
                              <div className={styles.toolStepList}>
                                {steps.map((s, si) => (
                                  <div key={si} className={styles.toolStep}>
                                    <span className={styles.toolStepDone}>✓</span>
                                    <span className={styles.toolStepLabelDone}>{s.label}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })()}
                      {msg.answer && (
                        <div
                          className={styles.aiAnswer}
                          onClick={handleAnswerClick}
                          dangerouslySetInnerHTML={{ __html: renderAnswer(msg.answer) }}
                        />
                      )}
                      {msg.chartSpec && (
                        <div className={styles.chartWrapper}>
                          <div className={styles.chartContainer}>
                            <PlotlyChart spec={msg.chartSpec} />
                          </div>
                          <button
                            className={styles.chartExpandBtn}
                            onClick={() => setExpandedChart(msg.chartSpec)}
                            title="Expand chart"
                          >
                            ⤢
                          </button>
                        </div>
                      )}
                      {showSqlEnabled && msg.sqlQueries?.length > 0 && (
                        <div className={styles.sqlSection}>
                          <button
                            className={styles.sqlToggle}
                            onClick={() => setShowSql(s => ({ ...s, [i]: !(s[i] ?? true) }))}
                          >
                            {(showSql[i] ?? true)
                              ? '▲ Hide SQL'
                              : `▼ View generated SQL${msg.sqlQueries.length > 1 ? ` (${msg.sqlQueries.length} queries)` : ''}`}
                          </button>
                          {(showSql[i] ?? true) && (
                            <div className={styles.sqlBlockList}>
                              {msg.sqlQueries.map((sql, qi) => (
                                <pre key={qi} className={styles.sqlBlock}><code>{sql}</code></pre>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {msg.source && (
                        <div className={styles.aiSource}>⌁ {msg.source}</div>
                      )}
                      {msg.chartSpec && (
                        <div className={styles.msgActions}>
                          <button
                            className={`${styles.pinBtn} ${pinnedMsgId === msg.id ? styles.pinBtnDone : ''}`}
                            onClick={() => pinMessage(msg, activeConv.messages[i - 1]?.text)}
                            title="Pin to dashboard"
                            disabled={pinnedMsgId === msg.id}
                          >
                            {pinnedMsgId === msg.id ? '✓ Pinned' : '⊕ Pin'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Polling banner — AI finished server-side, waiting for response */}
              {awaitingResponse && !loading && (
                <div className={`${styles.msg} ${styles.assistant}`}>
                  <div className={styles.assistantBubble}>
                    <div className={styles.aiLabel}>
                      <span className={styles.aiIcon}>◈</span> Structura AI
                    </div>
                    <div className={styles.awaitingBanner}>
                      <span className={styles.toolDot} />
                      Still working — checking every 5 seconds…
                    </div>
                  </div>
                </div>
              )}

              {/* Streaming response */}
              {loading && (
                <div className={`${styles.msg} ${styles.assistant}`}>
                  <div className={styles.assistantBubble}>
                    <div className={styles.aiLabel}>
                      <span className={styles.aiIcon}>◈</span> Structura AI
                      {isManageMode && <span className={styles.modeLabelInline}>Manage</span>}
                    </div>

                    {streamingText ? (
                      <div
                        className={styles.aiAnswer}
                        onClick={handleAnswerClick}
                        dangerouslySetInnerHTML={{ __html: renderAnswer(streamingText) }}
                      />
                    ) : !streamingChart && toolSteps.length === 0 ? (
                      <div className={styles.thinking}><span /><span /><span /></div>
                    ) : null}

                    {streamingChart && (
                      <div className={styles.chartWrapper}>
                        <div className={styles.chartContainer}>
                          <PlotlyChart spec={streamingChart} />
                        </div>
                        <button
                          className={styles.chartExpandBtn}
                          onClick={() => setExpandedChart(streamingChart)}
                          title="Expand chart"
                        >
                          ⤢
                        </button>
                      </div>
                    )}
                    {pendingNangoAuth && (
                      <button className={styles.nangoAuthBtn} onClick={handleNangoAuth}>
                        Authorize with {pendingNangoAuth.provider_display} →
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!loading && pendingNangoAuth && (
                <div className={styles.nangoAuthCard}>
                  <span className={styles.nangoAuthLabel}>
                    Ready to connect {pendingNangoAuth.provider_display}
                  </span>
                  <button className={styles.nangoAuthBtn} onClick={handleNangoAuth}>
                    Authorize →
                  </button>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {loading && (
              <div className={styles.statusStrip}>
                {(() => {
                  const nonError = toolSteps.filter(s => !s.error)
                  const active = nonError.find(s => !s.done) ?? nonError[nonError.length - 1]
                  return active ? (
                    <>
                      <span className={styles.toolDot} />
                      <span className={styles.statusStripText}>{active.label}</span>
                    </>
                  ) : (
                    <div className={styles.thinking}><span /><span /><span /></div>
                  )
                })()}
              </div>
            )}

            {tokenWarning && (
              <div className={styles.tokenWarning}>
                <span className={styles.tokenWarningText}>⚠ {tokenWarning}</span>
                <button className={styles.tokenWarningDismiss} onClick={() => setTokenWarning(null)}>✕</button>
              </div>
            )}

            <div className={styles.footer}>
              {attachment && (
                <div className={styles.attachmentChip}>
                  <span className={styles.attachmentIcon}>⊞</span>
                  <span className={styles.attachmentName}>{attachment.name}</span>
                  <span className={styles.attachmentMeta}>{attachment.totalLines} rows</span>
                  <button className={styles.attachmentRemove} onClick={clearAttachment} title="Remove">✕</button>
                </div>
              )}
              <div className={styles.inputBox}>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  className={styles.fileInput}
                  onChange={handleFileChange}
                />
                <button
                  className={styles.uploadBtn}
                  onClick={() => fileRef.current?.click()}
                  title="Attach file (CSV, JSON, TXT)"
                  disabled={loading}
                >
                  ⊕
                </button>
                <textarea
                  ref={inputRef}
                  className={styles.input}
                  rows={1}
                  placeholder={
                    attachment
                      ? 'Ask a question about the attached file…'
                      : isManageMode
                        ? 'Configure your data model or connect a data source…'
                        : 'Ask a question about your data…'
                  }
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={handleKey}
                  disabled={loading}
                />
                <button
                  className={`${styles.sendBtn} ${(query.trim() || attachment) ? styles.sendActive : ''}`}
                  onClick={() => handleSubmit()}
                  disabled={(!query.trim() && !attachment) || loading}
                >
                  ↵
                </button>
              </div>
              <div className={styles.hint}>
                {isManageMode
                  ? 'Manage Mode · dbt + S3 + Athena'
                  : 'Powered by Amazon Bedrock · Athena'}
              </div>
            </div>
          </>
        )}

      </div>

      {expandedChart && (
        <div className={styles.chartModal} onClick={() => setExpandedChart(null)}>
          <div className={styles.chartModalContent} onClick={e => e.stopPropagation()}>
            <button className={styles.chartModalClose} onClick={() => setExpandedChart(null)}>✕</button>
            <PlotlyChart
              spec={expandedChart}
              config={{ displayModeBar: true, responsive: true }}
              style={{ minHeight: '70vh' }}
            />
          </div>
        </div>
      )}
    </Shell>
  )
})

export default ChatBar
